import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { LoadPanel, Popup } from "devextreme-react";

import routes from "../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../common/Messages";
// The shared error-message ladder. NOT the local apiError helper the older sibling
// calculateDeferredRevenue still carries — that one is superseded.
import { getApiErrorMessage } from "../../../../common/concurrency";
import { useCommittedFilterRows } from "../../../../common/reportFilters";
import { DisableEnableControls, loadLookupFromStaticDataSource } from "../../../../common/Utils";
import { TransactionStatus } from "../../../../enum/Enum";
import FormField from "../../../../dynamicFormBuilder/FormField";
import CommonReport from "../../../../reports/components/CommonReport";
import OperationResultGrid from "../common/OperationResultGrid";
import { ParametersLayout } from "./Layout";
import { PREVIEW_COLUMNS, PREVIEW_KEY_EXPR } from "./PreviewGridColumns";
import "./generateRepeatingMiscCharges.css";
import {
    BUTTON,
    DATE_METHOD_OPTIONS,
    DAY_OF_NEXT_MONTH_MAX,
    DAY_OF_NEXT_MONTH_MIN,
    DEFAULT_DAY_OF_NEXT_MONTH,
    DEFAULT_LAYOUT_NAME,
    DEFAULT_SLIDING_DAYS,
    DateMethod,
    FIELD,
    HELP,
    HELP_TITLE,
    LAYOUT_ID,
    LAYOUT_NAME,
    PAGE_TITLE,
    PREVIEW_GRID_KEY,
    PREVIEW_TITLE,
    REPORT_FULL_NAME,
    REPORT_NAME,
    REQUEST_TIMEOUT_MS,
    RESULT_CAPTION,
    RESULT_LABELS,
    RESULT_OK,
    STATUS_OPTIONS,
} from "./constants";

const BUSY = Object.freeze({ IDLE: "idle", PREVIEWING: "previewing", STARTING: "starting" });

/**
 * Puts a class on the Preview popup's WRAPPER so ../common/operationScreen.css can reach
 * `.dx-popup-content` — the element DevExtreme sizes, and the one that has to become a flex column for
 * the grid to fill it. There is no other hook: the popup renders into an overlay outside this
 * component's DOM subtree.
 *
 * Module-level so the object identity is stable; an inline literal would be a new object every render
 * and make the Popup re-apply it.
 */
const PREVIEW_POPUP_ATTR = { class: "op-preview-popup" };

/**
 * A function, not a module-level constant: Reset must get a FRESH object every time, and a shared one
 * would be mutated across resets. Nothing here is time-dependent today (unlike the sibling screen's
 * new Date()) — the function form is defensive, so nobody "simplifies" it to a frozen constant and then
 * adds a date default later.
 *
 * Values are Delphi's defaults: FormAbLoadProgram sets cmbMethod.ItemIndex := 0 and
 * cmbStatus.ItemIndex := 2, SetDefaultSettings sets edtDay := 1 and edtSlidingDays := 0.
 */
const initialFormFields = () => ({
    DateMethod: DateMethod.Fixed,
    DayOfNextMonth: DEFAULT_DAY_OF_NEXT_MONTH,
    SlidingDays: DEFAULT_SLIDING_DAYS,
    Status: TransactionStatus.ReadyToCommitLater,
});

const isBlank = (value) => value === null || value === undefined || value === "";

/**
 * Delphi enforces 1..31 on the control (uWMD0094.dfm edtDay MinValue/MaxValue). Fields.js's NumberBox
 * takes no min/max from the layout schema and there is no util for setting arbitrary widget options, so
 * the rule lives here — and again server-side via [Range] on the request DTO, since a direct POST skips
 * this.
 *
 * MUST STAY IDEMPOTENT. handleFieldChange writes the clamped value back into a NumberBox that is
 * controlled by formFields, which fires onValueChanged again; clamp(clamp(x)) === clamp(x) is what stops
 * that being an infinite loop. This is the same recursion Delphi guards with a flag (fInSetDate on the
 * sibling WMD0012), and handleFieldChange additionally skips the write when the value is already
 * clamped, so the second pass usually never happens at all.
 *
 * null (blank) is a legal result, not a failure: a blank Day is exactly what Start confirmation 2 is for.
 */
const clampDayOfNextMonth = (value) => {
    if (isBlank(value)) {
        return null;
    }

    const day = Math.trunc(Number(value));
    if (!Number.isFinite(day)) {
        return null;
    }

    return Math.min(DAY_OF_NEXT_MONTH_MAX, Math.max(DAY_OF_NEXT_MONTH_MIN, day));
};

const toSlidingDays = (value) => {
    if (isBlank(value)) {
        return DEFAULT_SLIDING_DAYS;
    }

    const days = Math.trunc(Number(value));
    return Number.isFinite(days) ? days : DEFAULT_SLIDING_DAYS;
};

/**
 * "Did the user enter any filter criteria?", answered the way CommonReport answers it for itself in
 * getRangeFilters: a row counts only when `from` is set.
 *
 * An orderBy-only row deliberately does NOT count. It sorts the preview but contributes no predicate,
 * and Delphi's test is on wbWMReplicate.Where, which is predicates only. The server reaches the same
 * verdict independently, from the compiled clause.
 */
const hasFilterCriteria = (rows) => (rows || []).some((row) => row && !isBlank(row.from));

/**
 * The single row of the result dialog. WMD0094 hides the Batch half of TfrmABD0013 outright, because the
 * procedure creates no ACBatch row — so unlike the sibling screen there is nothing to show but the count.
 *
 * Tolerates a null result because the dialog's content is rendered unconditionally — see the Popup.
 */
const resultRows = (result) => [
    {
        label: RESULT_LABELS.TransactionsReplicated,
        value: (result ?? {}).TransactionsReplicated ?? "",
    },
];

/**
 * WM "Generate Repeating Misc Charges" (legacy WMD0094).
 *
 * Start runs SPWM_GENERATEREPEATINGMISCTRANSCHARGES server-side, replicating the matching recurring
 * miscellaneous WMTRANSACTIONHEADER / WMTRANSACTIONCHARGE rows with a recalculated transaction date, and
 * reports @OutTransReplicated. Preview runs the same filter through a read-only SELECT, which is what
 * the Delphi Preview button does.
 *
 * This is an OPERATION screen: no CustomStore, no <Editing>, no per-row read-modify-write, so
 * Architecture.md rules 4-6, 12 and 14-16 (including optimistic concurrency) do not apply.
 *
 * The legacy Help and Exit buttons are dropped: Help opened a Delphi context topic and Exit closed the
 * MDI child. Same decision as both sibling operation screens. The InsightTaskHelper / Task Agent
 * integration is also out of scope — scheduling lives in the TA module.
 *
 * There is deliberately NO field-level validate(): both selects always hold a value, and both numerics
 * are legitimately optional in the legacy. The only field rule is the Day range, applied by
 * clampDayOfNextMonth as the user leaves the field.
 */
const GenerateRepeatingMiscCharges = (props) => {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const formRefs = useRef({});
    const reportRef = useRef(null);
    const previewGrid = useRef(null);

    const [formFields, setFormFields] = useState(initialFormFields);
    const [filterWhereInfoList, setFilterWhereInfoList] = useState([]);
    const [busy, setBusy] = useState(BUSY.IDLE);

    /**
     * One object rather than three useStates. React 17 does not batch setState across an await, so three
     * separate updates after the fetch meant three extra renders of the page — and each one rebuilt the
     * grid's toolbar template, remounting GridLayoutManager and firing another GetGridLayouts request.
     */
    const [preview, setPreview] = useState({ rows: [], visible: false, hasRun: false });
    const [previewLayout, setPreviewLayout] = useState(DEFAULT_LAYOUT_NAME);

    const [result, setResult] = useState(null);

    const isBusy = busy !== BUSY.IDLE;

    const postOptions = useCallback(
        () => ({
            headers: {
                "Content-Type": "application/json",
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
            timeout: REQUEST_TIMEOUT_MS,
        }),
        [accessToken]
    );

    /**
     * CommonReport fires this once, from loadReport's .then. It stays correct because the editable filter
     * grid is bound to an ArrayStore wrapping this same array instance, which cell edits mutate in place —
     * so the reference captured here stays live. Do not clone it per render.
     */
    const handleReportStateChange = useCallback((reportState) => {
        setFilterWhereInfoList(reportState?.filterWhereInfoList ?? []);
    }, []);

    // Fixed enables Day of Next Month and kills # Of Days; Sliding does the reverse (cmbMethodChange).
    // DisableEnableControls takes `disabled`, not `enabled`, hence the negation.
    const applyMethodRules = useCallback((dateMethod) => {
        const fixed = dateMethod === DateMethod.Fixed;
        DisableEnableControls(FIELD.DayOfNextMonth, !fixed, formRefs);
        DisableEnableControls(FIELD.SlidingDays, fixed, formRefs);
    }, []);

    /**
     * ab_SafeSetFocus's guard clause, and the "Safe" in that Delphi helper's name is the point.
     *
     * A populated formRefs entry does NOT imply a live widget: ref.instance is always a function, but it
     * returns undefined while the DevExtreme widget behind it is being created or torn down. Reset hits
     * that window — it re-renders the fields, and a value change can land mid-remount — which threw
     * "Cannot read properties of undefined (reading 'focus')" and, because it happened inside a React
     * commit, took the whole page down rather than failing quietly.
     */
    const focusField = useCallback((name) => {
        const ref = formRefs.current?.[name];
        if (!ref || typeof ref.instance !== "function") {
            return;
        }

        const instance = ref.instance();
        if (instance && typeof instance.focus === "function") {
            instance.focus();
        }
    }, []);

    const handleFieldChange = useCallback(
        (name, value, e) => {
            if (name === FIELD.DateMethod) {
                // Runs for programmatic changes too, deliberately: after Reset the method returns to
                // Fixed and the enable/disable state has to follow it.
                applyMethodRules(value);

                // ab_SafeSetFocus on the field that just came alive — but ONLY when a person changed the
                // combo. DevExtreme populates e.event solely for UI-originated changes and leaves it
                // undefined when the value was set programmatically, which is what Reset's setFormFields
                // does. Without this test, Reset stole focus into the middle of the form and scrolled the
                // page — the very thing the comment here used to claim it avoided.
                if (e?.event) {
                    focusField(value === DateMethod.Sliding ? FIELD.SlidingDays : FIELD.DayOfNextMonth);
                }
            } else if (name === FIELD.DayOfNextMonth) {
                // Re-written only when it actually changed, so the controlled NumberBox does not fire
                // onValueChanged for a no-op. clampDayOfNextMonth is idempotent, so even if it did, the
                // second pass lands here and stops.
                const clamped = clampDayOfNextMonth(value);
                if (clamped !== value) {
                    setFormFields((previous) => ({ ...previous, DayOfNextMonth: clamped }));
                }
            }
            // SlidingDays and Status are absent on purpose: nothing derives from them, and this handler
            // must never react to a value it wrote itself.
        },
        [applyMethodRules, focusField]
    );

    const handleFormLoad = useCallback(() => {
        // FormField calls onFormLoad from fetchData's `finally`, BEFORE React has committed the child
        // Fields, so formRefs is still empty at that instant. Same 100ms shape as both siblings.
        setTimeout(() => {
            // Mandatory, not an optimisation: Fields.js renders a `select` with NO dataSource at all
            // (Architecture.md rule 3 — load the options via formRefs in onFormLoad).
            loadLookupFromStaticDataSource(FIELD.DateMethod, DATE_METHOD_OPTIONS, formFields, formRefs);
            loadLookupFromStaticDataSource(FIELD.Status, STATUS_OPTIONS, formFields, formRefs);
            applyMethodRules(formFields.DateMethod);
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Belt-and-braces re-apply, mirroring both sibling screens: onFormLoad fires once per FormField
    // mount, and the dynamic-form editor's save/discard path remounts the fields without re-firing it.
    useEffect(() => {
        applyMethodRules(formFields.DateMethod);
    }, [formFields.DateMethod, applyMethodRules]);

    /**
     * The client never picks which of the two numerics becomes @InDay — it sends both and the server maps
     * per DateMethod, exactly as MainAction does. Preview and Start therefore cannot resolve the date
     * differently.
     *
     * `extra` is spread LAST so handleStart can override DayOfNextMonth with the value the user
     * acknowledged in confirmation 2, and add the confirmation flags.
     */
    const buildRequest = useCallback(
        (rows, extra) => ({
            DateMethod: formFields.DateMethod,
            DayOfNextMonth: clampDayOfNextMonth(formFields.DayOfNextMonth),
            SlidingDays: toSlidingDays(formFields.SlidingDays),
            Status: formFields.Status,
            FilterWhereInfoList: rows || [],
            ...extra,
        }),
        [formFields]
    );

    // Commits any in-progress filter cell before reading the rows — see common/reportFilters.js for why
    // the onReportStateChange array is the wrong source at submit time.
    const readFilterRows = useCommittedFilterRows(reportRef, filterWhereInfoList);

    // Stable identities: an inline arrow here would change on every render and, via the Popup, keep the
    // memoised grid subtree re-rendering (and GridLayoutManager re-fetching).
    const hidePreview = useCallback(() => {
        setPreview((previous) => ({ ...previous, visible: false }));
    }, []);

    const closeResult = useCallback(() => setResult(null), []);

    const handlePreview = useCallback(async () => {
        // No confirmations: Delphi's Preview goes straight to wbWMReplicate.Preview and writes nothing.
        // Commits any in-progress filter cell first, so Preview matches what the user typed.
        const filterRows = await readFilterRows();

        setBusy(BUSY.PREVIEWING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMGenerateRepeatingMiscCharges.GetTransactions}`,
                JSON.stringify(buildRequest(filterRows)),
                postOptions()
            );

            const payload = response?.data;
            // DEV-5355 F2, same defect as the sibling Update Free Days screen this was modelled on: a
            // row array is the ONLY shape treated as success. Reading Resultdata straight into the grid
            // turned a server-side failure — a rejected filter, or a SQL error raised mid-query — into an
            // empty grid reporting "nothing matched", with the real message discarded.
            if (payload?.Success === false || !Array.isArray(payload?.Resultdata)) {
                setPreview({ rows: [], visible: false, hasRun: false });
                notify({ message: payload?.Message || MESSAGES.ERROR, width: 450 }, "error", 5000);
                return;
            }

            // An empty array IS legitimate — the grid says so via noDataText.
            setPreview({ rows: payload.Resultdata, visible: true, hasRun: true });
        } catch (error) {
            setPreview({ rows: [], visible: false, hasRun: false });
            notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [readFilterRows, buildRequest, postOptions]);

    /**
     * The three Start confirmations, in Delphi's order (btnStartClick).
     *
     * All three are computable from the form alone, so they are asked here and the answers ride along on
     * a single POST — there is no confirmation round-trip on this screen, which is why a double prompt or
     * an infinite loop is impossible by construction. (The sibling updateFreeDaysGracePeriods needs a
     * round-trip because its question depends on a database count the client cannot know.)
     *
     * The server re-evaluates every condition independently and refuses when an applicable flag is
     * absent, because a direct POST skips these dialogs — and confirmation 1 is genuinely destructive.
     *
     * Delphi chains the three in one boolean expression with `or`, so a Yes to an earlier question
     * short-circuits the later ones. That is a side effect of the expression shape rather than intent, so
     * it is not reproduced: every applicable question is asked. Any No still aborts.
     *
     * Returns null when the user backed out, otherwise the values and flags Start must send.
     */
    const confirmStart = useCallback(async (rows) => {
        // 1 - no filter: replicates EVERY recurring misc transaction in the company and stamps STAGE='B'
        //     on each source header. Tested against the COMMITTED rows, so a value the user typed without
        //     tabbing out no longer looks like an empty filter.
        if (!hasFilterCriteria(rows)
            && (await confirm(MESSAGES.REPEATING_CHARGES_NO_FILTER_CONFIRM, PAGE_TITLE)) !== true) {
            return null;
        }

        // 2 - Fixed with a blank Day. Scoped to Fixed, unlike the Delphi, which tested edtDay even in
        //     Sliding mode where its value is never used and the control is disabled.
        let dayOfNextMonth = clampDayOfNextMonth(formFields.DayOfNextMonth);
        if (formFields.DateMethod === DateMethod.Fixed && dayOfNextMonth === null) {
            if ((await confirm(MESSAGES.REPEATING_CHARGES_DAY_BLANK_CONFIRM, PAGE_TITLE)) !== true) {
                return null;
            }

            // Delphi writes the default into the field before running, so the user can see what was used.
            // Same here — and the same value is what gets posted.
            dayOfNextMonth = DEFAULT_DAY_OF_NEXT_MONTH;
            setFormFields((previous) => ({ ...previous, DayOfNextMonth: dayOfNextMonth }));
        }

        // 3 - Sliding with a blank, zero or negative offset. The procedure clamps anything below 1 to 1,
        //     so the generated date moves a day. The legacy message says otherwise; kept verbatim by
        //     team decision.
        if (formFields.DateMethod === DateMethod.Sliding
            && toSlidingDays(formFields.SlidingDays) <= 0
            && (await confirm(MESSAGES.REPEATING_CHARGES_SLIDING_DAYS_ZERO_CONFIRM, PAGE_TITLE)) !== true) {
            return null;
        }

        return {
            DayOfNextMonth: dayOfNextMonth,
            // Definite booleans, so the server can never ask back and this cannot loop.
            ConfirmNoFilterCriteria: true,
            ConfirmDefaultDayOfMonth: true,
            ConfirmZeroSlidingDays: true,
        };
    }, [formFields]);

    const handleStart = useCallback(async () => {
        // Read BEFORE the confirmations, not after: question 1 asks whether to run with no filter at all,
        // so it has to see the committed rows or it fires on a filter the user did type.
        const filterRows = await readFilterRows();

        const answers = await confirmStart(filterRows);
        if (answers === null) {
            return; // any No aborts, exactly like Delphi's Abort
        }

        setBusy(BUSY.STARTING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMGenerateRepeatingMiscCharges.Start}`,
                JSON.stringify(buildRequest(filterRows, answers)),
                postOptions()
            );

            const payload = response?.data;
            const data = payload?.Resultdata ?? {};

            // The dialog reports a run that created something; everything else is an error toast. That
            // covers a server-side refusal (validation, an over-long filter, or an unacknowledged
            // confirmation — none of which the client can reach) and a run that matched nothing.
            //
            // Note this is NOT what Delphi does: TfrmABD0013 opens either way, showing a blank box and
            // "No Records Found Within Given Range". A toast was preferred on the sibling screen and the
            // two are kept consistent.
            if (payload?.Resultdata == null || (data.TransactionsReplicated ?? 0) <= 0) {
                notify(
                    { message: payload?.Message || MESSAGES.NO_RECORD_FOUND_RANGE, width: 450 },
                    "error",
                    5000
                );
                return;
            }

            setResult(data);
        } catch (error) {
            notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [readFilterRows, confirmStart, buildRequest, postOptions]);

    const handleReset = useCallback(() => {
        // initialFormFields() is CALLED, not referenced: Reset needs a fresh object, not the identity of
        // a shared one (btnResetFilterClick -> wbWMReplicate.Reset + SetDefaultSettings).
        setFormFields(initialFormFields());
        setPreview({ rows: [], visible: false, hasRun: false });
        setResult(null);
        // Re-fetches the field definitions, rebuilds the filter store and re-fires onReportStateChange
        // with a fresh array. It does NOT remount FormField, so onFormLoad will not fire again — which is
        // correct: the imperatively-loaded SelectBox dataSources survive, and the method enable/disable
        // rule is re-applied by the useEffect above whenever DateMethod actually changed.
        if (reportRef.current && typeof reportRef.current.loadReport === "function") {
            reportRef.current.loadReport();
        }
    }, []);

    return (
        <>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={isBusy}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                message={
                    busy === BUSY.STARTING
                        ? MESSAGES.GENERATING_REPEATING_CHARGES
                        : MESSAGES.PREVIEWING_REPEATING_CHARGES
                }
            />

            {/*
                Delphi's pnlTopMessage / grpTopMessage is a centred bold title band above the
                where-builder. In the web app the page title lives in the toolbar, so the band collapses
                into this header rather than being reproduced as a separate strip.
            */}
            <div className="row">
                <div className="col-md-12 main-page">
                    <Toolbar className="header">
                        <Item location="before">
                            <div className="title">
                                <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>{PAGE_TITLE}</h6>
                            </div>
                        </Item>

                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Start} type="default" onClick={handleStart} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Preview} onClick={handlePreview} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Reset} onClick={handleReset} disabled={isBusy} />
                        </Item>
                    </Toolbar>
                </div>
            </div>

            <div className="grmc-page">
                <div className="op-filter">
                    <CommonReport
                        name={REPORT_NAME}
                        fullName={REPORT_FULL_NAME}
                        onReportStateChange={handleReportStateChange}
                        innerRef={reportRef}
                    />
                </div>

                {/*
                    Delphi's bottom split: grpBottomLeft (Parameters, alLeft) beside grpBottomMiddle
                    (Additional Information, alClient).
                */}
                <div className="op-parameters grmc-parameters">
                    <div className="grmc-fields">
                        {/*
                            hasOnlyOneColumn because there is exactly one group. Without it FormField
                            gives the group col-md-4, i.e. a third of THIS column rather than a third of
                            the page, which is unreadably narrow.
                        */}
                        <FormField
                            currrentUser={currentUser}
                            layoutId={LAYOUT_ID}
                            layoutName={LAYOUT_NAME}
                            defaultFormSchema={ParametersLayout}
                            formFields={formFields}
                            setformFields={setFormFields}
                            handleFieldValueChange={handleFieldChange}
                            DYNAMICFORMSETTING={dynamicFormSetting}
                            formRefs={formRefs}
                            onFormLoad={handleFormLoad}
                            hasOnlyOneColumn={true}
                        />
                    </div>

                    {/*
                        The two Delphi memos are one band here, re-texted per method rather than swapped
                        for a sibling control (cmbMethodChange toggles memMethodFixed / memMethodSliding
                        Visible). Same HELP-map-keyed-on-selection shape as calculateDeferredRevenue.
                        col-fieldset / list-title are FormField's own classes, so this box matches the
                        Parameters box beside it.
                    */}
                    <fieldset className="col-fieldset grmc-help">
                        <legend className="list-title">{HELP_TITLE}</legend>
                        <div className="op-help">{HELP[formFields.DateMethod]}</div>
                    </fieldset>
                </div>
            </div>

            <Popup
                visible={preview.visible}
                onHiding={hidePreview}
                hideOnOutsideClick={true}
                showCloseButton={true}
                title={PREVIEW_TITLE}
                width="94vw"
                height="88vh"
                wrapperAttr={PREVIEW_POPUP_ATTR}
            >
                {/*
                    The grid FILLS the popup — see .op-preview-popup in ../common/operationScreen.css.
                    It used to be height="calc(88vh - 144px)", where the 144 had to cover the popup's own
                    chrome PLUS the footer band below, and the two had to be kept in step by hand.
                    Flexing removes both problems: the grid takes whatever the footer leaves.

                    No ScrollView wrapper: the grid owns both scroll axes, and an outer scroller would
                    give it an unbounded width to lay out against, killing horizontal scrolling.
                */}
                <div className="op-preview-body">
                    <div className="op-preview-grid">
                        <OperationResultGrid
                            rows={preview.rows}
                            columns={PREVIEW_COLUMNS}
                            keyExpr={PREVIEW_KEY_EXPR}
                            noDataText={preview.hasRun ? MESSAGES.REPEATING_CHARGES_NO_TRANSACTIONS_FOUND : ""}
                            height="100%"
                            gridRef={previewGrid}
                            gridKey={PREVIEW_GRID_KEY}
                            currentLayout={previewLayout}
                            setCurrentLayout={setPreviewLayout}
                            accessToken={accessToken}
                        />
                    </div>

                    {/*
                    The row count, as TLookupForm shows it under its grid (SearchFm.pas lbInfo).

                    Counted from preview.rows, i.e. the whole result set — NOT the grid's current page or
                    its filter-row state. The grid pages at 50 client-side, so a page-based count would
                    read "50" for a 54-row result and contradict the number the user is checking against
                    AI8. Delphi counts qrySearch.RecordCount, which is the same thing.

                    Hidden at zero rows: the grid's noDataText already covers that, and the legacy's own
                    zero-row text is a different sentence entirely.
                */}
                    {preview.rows.length > 0 && (
                        <div className="op-preview-footer">
                            {MESSAGES.PREVIEW_RECORDS_MATCHING_FORMAT.replace("{0}", preview.rows.length)}
                        </div>
                    )}
                </div>
            </Popup>

            {/*
                The legacy result dialog, TfrmABD0013, with the Batch box hidden as WMD0094 hides it.

                OK is the only way out, matching the Delphi form: uABD0013.DFM sets BorderStyle bsSingle
                with BorderIcons reduced (no close box) and FormStyle fsStayOnTop, and it is shown with
                ShowModal — so there is no X and clicking away does nothing.
            */}
            <Popup
                visible={result != null}
                onHiding={closeResult}
                hideOnOutsideClick={false}
                showCloseButton={false}
                title={MESSAGES.PROCESS_COMPLETE}
                width={420}
                height="auto"
            >
                {/*
                    Rendered unconditionally. Wrapping this in `{result && …}` means that when DevExtreme
                    creates the Popup's content template `result` is still null, so it captures an empty
                    template and never fills it — the modal opens showing its title and nothing else.
                    resultRows tolerates null so the hidden state renders a harmless placeholder.
                */}
                <div className="op-result">
                    {/* Delphi shows the caller-supplied caption in a read-only memo above the count. */}
                    <div className="op-result-caption">{RESULT_CAPTION}</div>
                    {resultRows(result).map((row) => (
                        <div className="op-result-row" key={row.label}>
                            <span className="op-result-label">{row.label}</span>
                            <span className="op-result-value">{row.value}</span>
                        </div>
                    ))}
                    <div className="op-result-actions">
                        <Button text={RESULT_OK} type="default" onClick={closeResult} />
                    </div>
                </div>
            </Popup>
        </>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(GenerateRepeatingMiscCharges);
